import PropTypes from 'prop-types'
import clsx from 'clsx'
import Modal from '@/components/Modal'
import CardConfigure from '@/components/Modal/CardConfigure'
import Button from '@/components/Button'
import InformationCircle from '@/assets/svgs/information-circle.svg?react'

const DefaultEntry = ({ entry }) => (
  <div className="flex items-start gap-3 flex-1 min-w-0">
    {entry.icon && (
      <div className="shrink-0 inline-flex items-center justify-center pt-px bg-white [&_svg]:w-5 [&_svg]:h-5 [&_svg]:block [&_svg]:bg-white">
        {entry.icon}
      </div>
    )}
    <div className="flex-1 min-w-0 pb-3">
      {entry.heading && (
        <div className="text-sm font-bold text-grey-40 leading-5">{entry.heading}</div>
      )}
      {Array.isArray(entry.details) && entry.details.length > 0 && (
        <ul className="mt-1 p-0 list-none [&_li]:mt-0.5 [&_li]:text-[13px] [&_li]:text-grey-40 [&_li]:break-words">
          {entry.details.map((d, i) => (
            <li key={d.label ? `${d.label}-${i}` : i}>
              {d.label && <span className="text-grey-10 font-bold">{d.label}: </span>}
              <span>{d.value}</span>
            </li>
          ))}
        </ul>
      )}
      {entry.body && (
        <div className="mt-1 whitespace-pre-wrap wrap-break-word text-[13px] text-grey-40">
          {entry.body}
        </div>
      )}
      {entry.meta && <div className="mt-2 text-[11px] font-medium text-grey-50">{entry.meta}</div>}
    </div>
  </div>
)

DefaultEntry.propTypes = {
  entry: PropTypes.object.isRequired,
}

const ActivityLogModal = ({
  visible,
  close,
  entries = [],
  title = 'Activity Log',
  icon,
  renderEntry,
  emptyState = 'No activity to show',
  closeLabel = 'Close',
  className,
  dataTestId = 'activity-log-modal',
}) => {
  const renderEntries = () =>
    entries.map((entry, idx) => (
      <div className="flex relative mb-3.5 last:mb-0" key={entry.id ?? entry.key ?? idx}>
        {idx < entries.length - 1 && (
          <div
            className="absolute top-5 left-2.5 -bottom-3.5 w-[2.5px] pointer-events-none"
            style={{
              backgroundImage:
                'repeating-linear-gradient(to bottom, var(--color-grey-90) 0px, var(--color-grey-90) 4px, transparent 4px, transparent 8px)',
            }}
            aria-hidden="true"
          />
        )}
        {renderEntry ? renderEntry(entry, idx) : <DefaultEntry entry={entry} />}
      </div>
    ))

  return (
    <Modal visible={visible} variant="medium" close={close} dataTestId={dataTestId}>
      <CardConfigure>
        <CardConfigure.Header>
          <CardConfigure.Header.Title>
            <div className="flex items-center w-full">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-12 h-12 rounded-[10px] shrink-0 bg-theme-95 text-theme-50 [&_svg]:w-6.5 [&_svg]:h-6.5">
                  {icon || <InformationCircle />}
                </div>
                <div className="ml-4">
                  <div className="font-avenir-heavy type-header-4 text-grey-30">{title}</div>
                </div>
              </div>
            </div>
          </CardConfigure.Header.Title>
        </CardConfigure.Header>
        <CardConfigure.Body>
          <CardConfigure.Body.Col>
            <div
              className={clsx(
                'overflow-y-auto max-h-105 py-2 px-1 [scrollbar-width:thin]',
                className
              )}
            >
              {entries.length > 0 ? (
                renderEntries()
              ) : (
                <div className="p-4 text-center text-grey-50 text-sm">{emptyState}</div>
              )}
            </div>
          </CardConfigure.Body.Col>
        </CardConfigure.Body>
        <CardConfigure.Footer>
          <CardConfigure.Footer.Right>
            <Button round onClick={close}>
              {closeLabel}
            </Button>
          </CardConfigure.Footer.Right>
        </CardConfigure.Footer>
      </CardConfigure>
    </Modal>
  )
}

ActivityLogModal.propTypes = {
  visible: PropTypes.bool,
  close: PropTypes.func,
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      icon: PropTypes.node,
      heading: PropTypes.node,
      body: PropTypes.node,
      details: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.node,
          value: PropTypes.node,
        })
      ),
      meta: PropTypes.node,
    })
  ),
  title: PropTypes.node,
  icon: PropTypes.node,
  renderEntry: PropTypes.func,
  emptyState: PropTypes.node,
  closeLabel: PropTypes.string,
  className: PropTypes.string,
  dataTestId: PropTypes.string,
}

export default ActivityLogModal
